import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import { merge, startCase } from "lodash";
import { RRule } from "rrule";

import InputFormField from "components/input-form-field";
import SelectFormField from "components/select-form-field";

import {
  type BySetPos,
  MonthOption,
  RECURRENCE_TYPES,
  RECURRENCE_TYPE_DAILY,
  RECURRENCE_TYPE_MONTHLY,
  RECURRENCE_TYPE_WEEKLY,
  type RRuleResult,
  type RecurrenceStateValues,
  WEEKDAY_LABELS,
  WEEKDAY_ORDER,
  WeekDay,
  getInitializedValues,
  parseValues
} from "./utils";

const DEFAULT_VALUES: RecurrenceStateValues = {
  frequency: RECURRENCE_TYPE_WEEKLY,
  interval: 1,
  count: null,
  weekDays: [],
  monthOption: MonthOption.BY_MONTH_DAY,
  monthDays: null,
  monthWeekDay: null
};

interface RecurrenceRuleEditorProps {
  initialRule?: string;
  sx?: SxProps;
  open?: boolean;
  readOnly?: boolean;
  setOpen?: (value: boolean) => void;
  onCancel?: () => void;
  onSubmit?: (result: RRuleResult) => void;
}
export default function RecurrenceRuleEditor({
  initialRule,
  sx,
  open,
  readOnly,
  setOpen,
  onCancel,
  onSubmit: onSubmitResult
}: RecurrenceRuleEditorProps) {
  const defaultValues: RecurrenceStateValues = useMemo(() => {
    if (initialRule) {
      try {
        const rule = RRule.fromString(initialRule);
        return getInitializedValues(rule);
      } catch (e) {
        console.error("Error parsing RRULE:", e);
      }
    }

    return merge({}, DEFAULT_VALUES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRule, open]);

  const formMethods = useForm<RecurrenceStateValues>({
    mode: "onChange",
    defaultValues
  });

  const { handleSubmit, watch, formState, control, setValue, reset } =
    formMethods;
  const { isValid } = formState;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const state = watch();

  const handleWeekDayToggle = (day: WeekDay) => {
    const value = state.weekDays.includes(day)
      ? state.weekDays.filter((d) => d !== day)
      : [...state.weekDays, day];

    setValue("weekDays", value);
  };

  const onSubmit = useCallback(
    (data: RecurrenceStateValues) => {
      const result = parseValues(data, true);
      onSubmitResult?.(result);
    },
    [onSubmitResult]
  );

  const frequency = Number(state.frequency);

  const text = useMemo(() => {
    const result = parseValues(state)?.text;
    return result ? startCase(result) : "";
  }, [state]);

  return (
    <Stack sx={{ p: open ? 2 : 1, ...sx }} rowGap={2} maxWidth="520px">
      {!open ? (
        <Stack
          direction="row"
          width="100%"
          columnGap={2}
          sx={{
            cursor: "pointer"
          }}
          onClick={() => {
            if (!readOnly) {
              setOpen?.(!open);
            }
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {text}
          </Typography>
          {setOpen && !readOnly && (
            <IconButton size="small" sx={{ padding: 0.5 }}>
              <EditIcon sx={{ width: 14, height: 14 }} />
            </IconButton>
          )}
        </Stack>
      ) : (
        <>
          <SelectFormField
            options={RECURRENCE_TYPES.getOptions()}
            name="frequency"
            label="Recurrence"
            control={control}
            required
          />

          {Boolean(state.frequency) && (
            <Stack direction="row" alignItems="center">
              <InputFormField
                type="number"
                name="interval"
                label="Repeat every"
                control={control}
                sx={{ width: 130, mr: 1 }}
                inputProps={{ min: 1 }}
                required
              />

              <Typography variant="body1" mt={2}>
                {frequency === RECURRENCE_TYPE_DAILY
                  ? "day(s)"
                  : frequency === RECURRENCE_TYPE_WEEKLY
                    ? "week(s)"
                    : frequency === RECURRENCE_TYPE_MONTHLY
                      ? "month(s)"
                      : "year(s)"}
              </Typography>
            </Stack>
          )}

          {frequency === RECURRENCE_TYPE_WEEKLY && (
            <Box>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  color: grey[900]
                }}
              >
                On days
              </Typography>
              <ButtonGroup variant="outlined" sx={{ mt: 1 }}>
                {WEEKDAY_ORDER.map((day) => (
                  <Button
                    key={day}
                    size="small"
                    variant={
                      state.weekDays.includes(day) ? "contained" : "outlined"
                    }
                    onClick={() => handleWeekDayToggle(day)}
                    sx={{ width: "50px" }}
                  >
                    {day}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          )}

          {frequency === RECURRENCE_TYPE_MONTHLY && (
            <Box mt={1}>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  color: grey[900]
                }}
              >
                Recur on *
              </Typography>
              <RadioGroup
                value={state.monthOption}
                onChange={(e) => {
                  const value = e.target.value as MonthOption;
                  if (value === MonthOption.BY_SET_POS) {
                    setValue("monthWeekDay", {
                      pos: (state.monthWeekDay?.pos || 1) as BySetPos,
                      day: (state.monthWeekDay?.day || WeekDay.MO) as WeekDay
                    });
                  } else if (value === MonthOption.BY_MONTH_DAY) {
                    setValue("monthDays", 1);
                  }
                  setValue("monthOption", value);
                }}
              >
                <FormControlLabel
                  value={MonthOption.BY_MONTH_DAY}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography sx={{ width: "90px" }}>
                        Day of month:
                      </Typography>
                      <InputFormField
                        name="monthDays"
                        type="number"
                        inputProps={{
                          min: -31,
                          max: 31
                        }}
                        control={control}
                        sx={{ width: 90, mr: 1 }}
                        required={
                          state.monthOption === MonthOption.BY_MONTH_DAY
                        }
                        disabled={
                          state.monthOption !== MonthOption.BY_MONTH_DAY
                        }
                      />
                    </Stack>
                  }
                />

                <FormControlLabel
                  value={MonthOption.BY_SET_POS}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body1" sx={{ width: "90px" }}>
                        The
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 153 }}>
                        <Select
                          disabled={
                            state.monthOption !== MonthOption.BY_SET_POS
                          }
                          value={state.monthWeekDay?.pos || 1}
                          onChange={(e) => {
                            setValue("monthWeekDay", {
                              ...(state.monthWeekDay ||
                                DEFAULT_VALUES.monthWeekDay!),
                              pos: parseInt(
                                e.target.value as string
                              ) as BySetPos
                            });
                          }}
                        >
                          <MenuItem value={1}>First</MenuItem>
                          <MenuItem value={2}>Second</MenuItem>
                          <MenuItem value={3}>Third</MenuItem>
                          <MenuItem value={4}>Fourth</MenuItem>
                          <MenuItem value={-1}>Last</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          disabled={
                            state.monthOption !== MonthOption.BY_SET_POS
                          }
                          value={state.monthWeekDay?.day || "MO"}
                          onChange={(e) => {
                            setValue("monthWeekDay", {
                              ...(state.monthWeekDay ||
                                DEFAULT_VALUES.monthWeekDay!),
                              day: e.target.value as WeekDay
                            });
                          }}
                        >
                          {Object.entries(WEEKDAY_LABELS).map(
                            ([value, label]) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={MonthOption.LAST_DAY}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body1" sx={{ width: "120px" }}>
                        Last day of month
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </Box>
          )}

          <Typography variant="body3" sx={{ my: 1, fontWeight: 700 }}>
            {text}
          </Typography>

          <Stack
            width="100%"
            direction="row"
            columnGap={2}
            justifyContent="space-between"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={onCancel}
              disabled={!isValid}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid}
              sx={{ width: "100px" }}
            >
              Apply
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
